import {forwardRef} from 'react';
import styled from '@emotion/styled';

import theme from 'theme';
import {SizeTokenValue} from 'common';
import {Loader} from 'Loader';

import {LinkVariant} from './types';

import type {LinkProps, LinkStyles} from './types';
import type {MouseEventHandler} from 'react';

const variantBehavior = {
  [LinkVariant.Accent]: {
    default: {
      color: theme.colors.accent[90],
    },

    hover: {
      color: theme.colors.accent[80],
    },

    active: {
      color: theme.colors.accent[100],
    },

    visited: {
      color: theme.colors.additional.violet[80],
    },

    disabled: {
      color: theme.colors.grey[40],
      cursor: 'default',
    },
  },

  [LinkVariant.Blue]: {
    default: {
      color: theme.colors.additional.blue[90],
    },

    hover: {
      color: theme.colors.additional.blue[80],
    },

    active: {
      color: theme.colors.additional.blue[100],
    },

    visited: {
      color: theme.colors.additional.violet[80],
    },

    disabled: {
      color: theme.colors.grey[40],
      cursor: 'default',
    },
  },
};

const BaseLinkComponent = styled.a<LinkStyles>`
  display: inline-flex;
  gap: 8px;
  align-items: center;
  text-decoration-skip-ink: none;
  vertical-align: bottom;
  cursor: pointer;
  user-select: none;
  font-family: ${theme.typography.p1.fontFamily};

  ${props => props.behavior.default}

  :hover {
    ${props => props.behavior.hover}
  }

  :active {
    ${props => props.behavior.active}
  }

  :visited {
    ${props => props.isVisitable && props.behavior.visited}
  }

  &[disabled] {
    ${props => props.behavior.disabled}
  }
`;

const LinkComponent = styled(BaseLinkComponent)`
  text-decoration: none;

  :link {
    text-decoration: none;
  }

  :hover {
    text-decoration: underline;
  }

  :active {
    text-decoration: none;
  }

  :visited {
    text-decoration: none;
  }

  &[disabled] {
    text-decoration: none;
  }
`;

const PseudoLinkComponent = styled(BaseLinkComponent)`
  text-decoration: underline dashed;

  :link {
    text-decoration: underline dashed;
  }

  :hover {
    text-decoration: underline dashed;
  }

  :active {
    text-decoration: dashed;
  }

  :visited {
    text-decoration: dashed;
  }

  &[disabled] {
    text-decoration: none;
  }
`;

export const Link = forwardRef<HTMLAnchorElement, LinkProps>((props, ref) => {
  const {
    children,
    disabled,
    behavior,
    isPseudolink,
    isLoading,

    variant = LinkVariant.Accent,
    qa = 'link',

    ...rest
  } = props;

  const onClickCapture: MouseEventHandler<HTMLAnchorElement> = e => {
    if (disabled) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!props.href) {
      e.preventDefault();
    }

    props.onClickCapture?.(e);
  };

  const linkStyles = {
    ...rest,

    disabled,
    onClickCapture,

    behavior: behavior ?? variantBehavior[variant],

    'data-qa': qa,
  };

  const Component = isPseudolink ? PseudoLinkComponent : LinkComponent;

  return (
    <Component ref={ref} {...linkStyles}>
      {isLoading && <Loader color="inherit" size={SizeTokenValue.Small} />}
      {children}
    </Component>
  );
});
